import {
  SET_MCPB_CONF,
  SET_MCPB_SLOTS,
  SET_MCPB_SLOT_CAMERA,
  SET_MCPB_TRACK_EVENTS,
  SET_MCPB_EVENTS_LOADING,
  SET_MCPB_EVENTS_ERROR,
  SET_MCPB_SEARCH,
  SET_MCPB_PLAYHEAD,
  SET_MCPB_EVENT_FLAG,
  RESET_MCPB,
  LOGOUT_USER,
  EXPIRE_SESSION,
  JUMP_SYSTEM_START,
} from '../actions/types';
import { DEFAULT_WINDOW, DEFAULT_FILTERS } from '../util/mcpbTime';

/**
 * MCPlaybackReducer — session-only state for the multi-cam playback view.
 *
 * Session-only by design (NOT in the redux-persist whitelist): slots + trackEvents +
 * activeLayoutId are wiped on logout / session-expire / jump-system-start (the playhead and
 * camera set from one server are meaningless on another — mirrors the jump-flicker fix). Saved
 * layouts (Phase 2e) live in localStorage, separate from this slice.
 *
 * Shape:
 *   conf            1 | 2 | 4 — grid size (number of slots)
 *   slots           array(conf) of camera {bSerial,bCamera,...} or null (empty cell)
 *   trackEvents     map keyed `${serial}_${cameraId}` -> array of frozen events (from loadMultiCamEvents)
 *   trackEventsWindow  'start|end' the trackEvents map was fetched for ('' = unknown). A fetch for the SAME
 *                      window merges per camera (a set swap keeps the other sets' events); any other
 *                      window replaces the map (86bc8571y).
 *   eventsLoading   boolean — a getEventsByTime fan-out is in flight
 *   activeLayoutId  id of the loaded saved layout, or null (Custom/unsaved)
 *
 * Committed time-nav search (set by the dt-modal's Go via SET_MCPB_SEARCH — see runMcpbSearch):
 *   selectedTimestamp  NVR-local 'YYYYMMDDHHMMSS' anchor = window START (post-lookback), or null
 *   window             'Show window' key (5min|15min|1hr|6hr|24hr) — the timeline span (1f)
 *   filters            'Filter by' set. Default ALL OFF (get everything; selecting a chip restricts).
 *                      v1 filtering is CLIENT-SIDE (event.getEventsByTime has no flag/tag params —
 *                      it returns all events in the window; Java MCEventModel filters client-side
 *                      off the frozen event's sTags/fArchive/fExport). Applied at the timeline (1f).
 *   playheadOffset     last playhead position (seconds from window start), so a same-session nav away
 *                      and back restores the EXACT moment (1g retention). Wiped with the rest on a
 *                      server change — a playhead position is meaningless on another NVR.
 */
const INITIAL_STATE = {
  conf: 1,
  slots: [null],
  trackEvents: {},
  trackEventsWindow: '',
  eventsLoading: false,
  eventsError: false,
  activeLayoutId: null,
  selectedTimestamp: null,
  window: DEFAULT_WINDOW,
  filters: DEFAULT_FILTERS,
  playheadOffset: 0,
};

// Clear the session-scoped fields but keep the grid size (conf).
const wipe = (state) => ({
  ...INITIAL_STATE,
  conf: state.conf,
  slots: Array(state.conf).fill(null),
});

// Resize slots to `conf`, preserving existing cameras (pad with null / truncate).
const resizeSlots = (slots, conf) =>
  Array.from({ length: conf }, (_, i) => (i < slots.length ? slots[i] : null));

export default function mcPlaybackReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case SET_MCPB_CONF:
      return { ...state, conf: action.payload, slots: resizeSlots(state.slots, action.payload) };

    case SET_MCPB_SLOTS:
      return { ...state, slots: action.payload };

    case SET_MCPB_SLOT_CAMERA: {
      const slots = state.slots.slice();
      slots[action.payload.index] = action.payload.camera;
      return { ...state, slots };
    }

    case SET_MCPB_TRACK_EVENTS:
      return { ...state, trackEvents: action.payload, trackEventsWindow: action.window || '' };

    case SET_MCPB_EVENTS_LOADING:
      // Starting a fetch (payload=true) clears any prior error so a retry's outcome is judged fresh.
      return { ...state, eventsLoading: action.payload, ...(action.payload ? { eventsError: false } : {}) };

    case SET_MCPB_EVENTS_ERROR:
      return { ...state, eventsError: action.payload };

    case SET_MCPB_SEARCH: {
      const { selectedTimestamp, window, filters } = action.payload;
      return {
        ...state,
        selectedTimestamp,
        window: window || state.window,
        filters: filters || state.filters,
      };
    }

    case SET_MCPB_PLAYHEAD: {
      const n = Number(action.payload);
      return { ...state, playheadOffset: Number.isFinite(n) ? n : 0 };
    }

    // Optimistic archive/export toggle (2c): flip the matching event's pre-computed boolean across
    // every track (bID is unique, but scanning all tracks keeps it conf-agnostic). flag 1 -> fArchive,
    // flag 2 -> fExport (FLAG_ARCHIVE=1 / FLAG_EXPORT=2, verified flags.py). The Archive/Export action
    // buttons read these booleans for their filled/active status (D-cellchrome-merge: no separate
    // status row). The setMcpbEventFlag thunk dispatches this before the RPC and dispatches the inverse
    // to REVERT on failure. No-op (same reference) when no event matches — nothing re-renders.
    case SET_MCPB_EVENT_FLAG: {
      const { bID, flag, value, serial } = action.payload;
      const field = flag === 2 ? 'fExport' : 'fArchive';
      // Track keys are `${serial}_${bCamera}`. Event bIDs are a PER-NVR auto-increment, so bID alone is
      // NOT unique across an eView grid: archiving A:50 also flipped B:50 locally while only A was
      // persisted, leaving B falsely archived until the next fetch. Scope the flip to the owning NVR when
      // the caller told us which one it is; with no serial (single-NVR callers) keep the old broad match.
      // #86bb7phhh
      const ownsEvent = (key) => serial == null || String(key).indexOf(`${serial}_`) === 0;
      let changed = false;
      const trackEvents = {};
      Object.keys(state.trackEvents || {}).forEach((key) => {
        if (!ownsEvent(key)) { trackEvents[key] = state.trackEvents[key]; return; }
        trackEvents[key] = state.trackEvents[key].map((ev) => {
          if (ev.bID !== bID) return ev;
          changed = true;
          return { ...ev, [field]: !!value };
        });
      });
      return changed ? { ...state, trackEvents } : state;
    }

    case RESET_MCPB:
    case LOGOUT_USER:
    case EXPIRE_SESSION:
    case JUMP_SYSTEM_START:
      return wipe(state);

    default:
      return state;
  }
};
